/**
 * Bulle de recherche globale (navigation rapide)
 *
 * Rôle : Bouton flottant qui ouvre un modal de recherche par mot-clé. Index statique de pages et tuiles ;
 * appelle /api/chatbot/search pour la recherche sémantique. Redirige vers la page ou la tuile choisie.
 * Technologies : React, Next.js (useRouter), API chatbot/search
 */
"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Search, X, ArrowRight } from "lucide-react";

type SearchItem = {
  id: string;
  title: string;
  description: string;
  href: string;
  type: "page" | "tile";
  category?: string;
};

const SEARCH_INDEX: SearchItem[] = [
  // Dashboard
  {
    id: "dashboard",
    title: "Dashboard",
    description: "Page d'accueil avec les principales fonctionnalités",
    href: "/dashboard",
    type: "page",
    category: "Accueil"
  },
  {
    id: "vision",
    title: "Notre vision",
    description: "Statistiques et vision pour les TPE/PME en France",
    href: "/vision",
    type: "page",
    category: "Informations"
  },
  
  // Tuiles Dashboard
  {
    id: "siteweb",
    title: "Votre site web e-commerce",
    description: "Vendez directement vos produits en stock grâce à un site e-commerce personnalisé",
    href: "#",
    type: "tile",
    category: "Services"
  },
  {
    id: "erp",
    title: "Logiciel de gestion d'entreprise",
    description: "Un ERP/CRM complet : gestion des devis, commandes, factures, stocks, projets",
    href: "#",
    type: "tile",
    category: "Services"
  },
  {
    id: "synergy",
    title: "Synergy Support",
    description: "Accédez aux fonctionnalités clés de la plateforme",
    href: "/communaute",
    type: "tile",
    category: "Services"
  },

  // Synergy Support
  {
    id: "communaute",
    title: "Synergy Support",
    description: "Plateforme complète de gestion d'entreprise",
    href: "/communaute",
    type: "page",
    category: "Services"
  },
  {
    id: "stock-partenaires",
    title: "Stock Partenaires",
    description: "Accédez aux stocks des partenaires en contrat",
    href: "/dashboard/ecommerce/marketplace",
    type: "tile",
    category: "Synergy Support"
  },
  {
    id: "listing-entreprises",
    title: "Listing d'entreprises",
    description: "Annuaire des partenaires et entreprises du secteur marine",
    href: "/communaute/listing-entreprises",
    type: "tile",
    category: "Synergy Support"
  },
  {
    id: "academie",
    title: "Procédures & Vidéos Produits",
    description: "Procédures filmées/écrites + formations produits & matières",
    href: "/communaute/academie",
    type: "tile",
    category: "Synergy Support"
  },
  {
    id: "echanges",
    title: "Vos Échanges",
    description: "Visio, Emails, Communauté, Chats...",
    href: "/communaute/echanges",
    type: "tile",
    category: "Synergy Support"
  },
  {
    id: "offres",
    title: "Offres",
    description: "Emplois, annonces de vente/rachat et appels d'offres",
    href: "/communaute/offres",
    type: "tile",
    category: "Synergy Support"
  },
  {
    id: "transport",
    title: "Transport",
    description: "Organisez vos expéditions : colis, adresses, tarifs & étiquettes",
    href: "/communaute/transport",
    type: "tile",
    category: "Synergy Support"
  },
  {
    id: "calendrier",
    title: "Calendrier",
    description: "Gérez vos rendez-vous, événements et planning avec un calendrier interactif",
    href: "/communaute/calendrier",
    type: "tile",
    category: "Synergy Support"
  },
  {
    id: "projets",
    title: "Mes projets",
    description: "Suivez vos projets marins : devis, commandes, expéditions, factures et tracking",
    href: "/communaute/projets",
    type: "tile",
    category: "Synergy Support"
  },
  {
    id: "fournisseurs",
    title: "Vos fournisseurs",
    description: "Annuaire fournisseurs (confidentiel) : recherche, filtres, contacts",
    href: "/communaute/fournisseurs",
    type: "tile",
    category: "Synergy Support"
  },

  // Académie - Rubriques principales
  {
    id: "academie-commercial",
    title: "Académie - Commercial",
    description: "Toutes vos procédures commerciales : de la création d'un produit jusqu'à la gestion complète des devis, commandes et expéditions",
    href: "/communaute/academie/procedures/commercial",
    type: "page",
    category: "Académie"
  },
  {
    id: "academie-achats",
    title: "Académie - Achats",
    description: "Procédures liées aux achats : demandes de prix, commandes fournisseurs et mise à jour tarifaire",
    href: "/communaute/academie/procedures/achats",
    type: "page",
    category: "Académie"
  },
  {
    id: "academie-comptabilite",
    title: "Académie - Comptabilité",
    description: "Toutes vos procédures comptables : saisie des factures, liaison des écritures et gestion du factor",
    href: "/communaute/academie/procedures/comptabilite",
    type: "page",
    category: "Académie"
  },
  {
    id: "academie-informatique",
    title: "Académie - Informatique",
    description: "Procédures informatiques : export de données, champs personnalisés, import en masse, gestion des utilisateurs",
    href: "/communaute/academie/procedures/informatique",
    type: "page",
    category: "Académie"
  },
  {
    id: "academie-magasin",
    title: "Académie - Magasin",
    description: "Procédures magasin : de la réception des commandes au rangement et suivi des stocks",
    href: "/communaute/academie/procedures/magasin",
    type: "page",
    category: "Académie"
  },
  {
    id: "academie-rh",
    title: "Académie - Ressources Humaines",
    description: "Procédures RH : gestion des salaires, bulletins de paie et opérations liées aux employés",
    href: "/communaute/academie/procedures/rh",
    type: "page",
    category: "Académie"
  },

  // Procédures Commercial
  {
    id: "creer-produit",
    title: "Créer un produit",
    description: "Guide pour créer un nouveau produit dans le système",
    href: "/communaute/academie/procedures/commercial/creer-un-produit",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "creer-reference",
    title: "Créer une référence",
    description: "Procédure pour créer une nouvelle référence produit",
    href: "/communaute/academie/procedures/commercial/creer-une-reference",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "creer-tiers",
    title: "Créer un tiers",
    description: "Guide pour créer un nouveau tiers (client ou fournisseur)",
    href: "/communaute/academie/procedures/commercial/creer-un-tiers",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "creer-projet",
    title: "Créer un projet",
    description: "Procédure pour créer et gérer un nouveau projet",
    href: "/communaute/academie/procedures/commercial/creer-un-projet",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "proposition-commerciale",
    title: "Faire une proposition commerciale",
    description: "Guide pour créer une proposition commerciale efficace",
    href: "/communaute/academie/procedures/commercial/faire-proposition-commerciale",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "actualisation-devis",
    title: "Créer des actualisations de devis",
    description: "Procédure pour actualiser les devis existants (module supplémentaire)",
    href: "/communaute/academie/procedures/commercial/creer-actualisation-devis",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "commande-client",
    title: "Faire une commande client",
    description: "Guide pour traiter une commande client",
    href: "/communaute/academie/procedures/commercial/faire-commande-client",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "confirmation-commande",
    title: "Attacher une confirmation de commande mail",
    description: "Procédure pour attacher une confirmation de commande par mail dans Dolibarr",
    href: "/communaute/academie/procedures/commercial/attacher-confirmation-commande",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "creer-expedition",
    title: "Créer une expédition",
    description: "Guide pour créer et gérer une expédition",
    href: "/communaute/academie/procedures/commercial/creer-expedition",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "devis-commandes-expeditions-factures",
    title: "Devis - Commandes - Expéditions - Factures",
    description: "Processus complet de gestion des devis aux factures",
    href: "/communaute/academie/procedures/commercial/devis-commandes-expeditions-factures",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "avoir-facture-client",
    title: "Création d'avoir facture client & remise en stock",
    description: "Procédure pour créer un avoir et remettre en stock le produit",
    href: "/communaute/academie/procedures/commercial/avoir-facture-client",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "dilution-frais-transport",
    title: "Dilution des frais de transport",
    description: "Guide pour diluer les frais de transport dans les factures",
    href: "/communaute/academie/procedures/commercial/dilution-frais-transport",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "suivi-mails",
    title: "Avoir un suivi de mails",
    description: "Procédure pour gérer le suivi des emails",
    href: "/communaute/academie/procedures/commercial/mails",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "expedition-envoi-net",
    title: "Expédition \"Envoi du Net\"",
    description: "Guide pour gérer les expéditions via le réseau",
    href: "/communaute/academie/procedures/commercial/expedition-envoi-du-net",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "ouvrir-compte-pro",
    title: "Ouvrir un compte pro",
    description: "Procédure pour ouvrir un compte professionnel",
    href: "/communaute/academie/procedures/commercial/ouvrir-compte-pro",
    type: "tile",
    category: "Académie - Commercial"
  },
  {
    id: "siteweb-preparation-commandes",
    title: "Site web - Préparation des commandes web en compte",
    description: "Guide pour préparer les commandes web dans le compte",
    href: "/communaute/academie/procedures/commercial/siteweb-preparation-commandes-compte",
    type: "tile",
    category: "Académie - Commercial"
  },

  // Procédures Achats
  {
    id: "commande-fournisseur",
    title: "Commande fournisseur",
    description: "Guide pour créer et gérer une commande fournisseur",
    href: "/communaute/academie/procedures/achats/commande-fournisseur",
    type: "tile",
    category: "Académie - Achats"
  },
  {
    id: "demande-prix",
    title: "Faire une demande de prix fournisseur",
    description: "Procédure pour demander des prix aux fournisseurs",
    href: "/communaute/academie/procedures/achats/demande-prix",
    type: "tile",
    category: "Académie - Achats"
  },
  {
    id: "mise-a-jour-tarifs",
    title: "Mise à jour tarifaire fournisseurs",
    description: "Guide pour mettre à jour les tarifs fournisseurs",
    href: "/communaute/academie/procedures/achats/mise-a-jour-tarifs",
    type: "tile",
    category: "Académie - Achats"
  },

  // Procédures Comptabilité
  {
    id: "entree-tickets-comptabilite",
    title: "Entrée des tickets en comptabilité",
    description: "Guide pour saisir les tickets en comptabilité",
    href: "/communaute/academie/procedures/comptabilite/entree-des-tickets-en-comptabilite",
    type: "tile",
    category: "Académie - Comptabilité"
  },
  {
    id: "facture-fournisseur",
    title: "Facture fournisseur",
    description: "Procédure pour traiter une facture fournisseur",
    href: "/communaute/academie/procedures/comptabilite/facture-fournisseur",
    type: "tile",
    category: "Académie - Comptabilité"
  },
  {
    id: "facture-fournisseur-depuis-commande",
    title: "Facture fournisseur à partir d'une commande fournisseur",
    description: "Guide pour créer une facture à partir d'une commande fournisseur",
    href: "/communaute/academie/procedures/comptabilite/facture-fournisseur-depuis-commande-fournisseur",
    type: "tile",
    category: "Académie - Comptabilité"
  },
  {
    id: "facture-fournisseur-depuis-commande-telephonique",
    title: "Facture fournisseur à partir d'une commande téléphonique ou mail",
    description: "Procédure pour traiter une facture depuis une commande téléphonique ou mail",
    href: "/communaute/academie/procedures/comptabilite/facture-fournisseur-depuis-commande-telephonique-mail",
    type: "tile",
    category: "Académie - Comptabilité"
  },
  {
    id: "liaison-comptable-lignes-factures",
    title: "Liaison comptable des lignes de factures clients/fournisseurs",
    description: "Guide pour lier les lignes de factures en comptabilité",
    href: "/communaute/academie/procedures/comptabilite/liaison-comptable-lignes-factures",
    type: "tile",
    category: "Académie - Comptabilité"
  },
  {
    id: "creer-factures-fournisseurs",
    title: "Créer une factures fournisseurs",
    description: "Procédure pour créer des factures fournisseurs",
    href: "/communaute/academie/procedures/comptabilite/creer-une-factures-fournisseurs",
    type: "tile",
    category: "Académie - Comptabilité"
  },
  {
    id: "creer-compte-client-factor",
    title: "Créer un compte client Factor",
    description: "Guide pour créer un compte client Factor",
    href: "/communaute/academie/procedures/comptabilite/creer-compte-client-factor",
    type: "tile",
    category: "Académie - Comptabilité"
  },
  {
    id: "procedure-factor",
    title: "Procédure Factor",
    description: "Procédure complète pour gérer le Factor",
    href: "/communaute/academie/procedures/comptabilite/procedure-factor",
    type: "tile",
    category: "Académie - Comptabilité"
  },

  // Procédures Informatique
  {
    id: "creer-acces-portail-client",
    title: "Créer un accès Portail Client",
    description: "Guide pour créer un accès au portail client (module supplémentaire)",
    href: "/communaute/academie/procedures/informatique/creer-acces-portail-client",
    type: "tile",
    category: "Académie - Informatique"
  },
  {
    id: "exporter-donnees",
    title: "Exporter des données",
    description: "Procédure pour exporter des données du système",
    href: "/communaute/academie/procedures/informatique/exporter-donnees",
    type: "tile",
    category: "Académie - Informatique"
  },
  {
    id: "ajouter-champs-personnalises",
    title: "Ajouter des champs personnalisés",
    description: "Guide pour ajouter des champs personnalisés",
    href: "/communaute/academie/procedures/informatique/ajouter-champs-personnalises",
    type: "tile",
    category: "Académie - Informatique"
  },
  {
    id: "import-masse-produits",
    title: "Import en masse de produits",
    description: "Procédure pour importer des produits en masse",
    href: "/communaute/academie/procedures/informatique/import-masse-produits",
    type: "tile",
    category: "Académie - Informatique"
  },
  {
    id: "maj-donnees-masse",
    title: "Mettre à jour des données en masse",
    description: "Guide pour mettre à jour des données en masse",
    href: "/communaute/academie/procedures/informatique/maj-donnees-masse",
    type: "tile",
    category: "Académie - Informatique"
  },
  {
    id: "lier-fournisseurs-produits",
    title: "Lier des fournisseurs aux produits",
    description: "Procédure pour lier des fournisseurs aux produits",
    href: "/communaute/academie/procedures/informatique/lier-fournisseurs-produits",
    type: "tile",
    category: "Académie - Informatique"
  },
  {
    id: "creer-compte-utilisateur",
    title: "Créer un compte utilisateur",
    description: "Guide pour créer un nouveau compte utilisateur",
    href: "/communaute/academie/procedures/informatique/creer-compte-utilisateur",
    type: "tile",
    category: "Académie - Informatique"
  },
  {
    id: "base-donnees-etiquettes",
    title: "Créer une base de données pour étiquettes",
    description: "Procédure pour créer une base de données d'étiquettes",
    href: "/communaute/academie/procedures/informatique/base-donnees-etiquettes",
    type: "tile",
    category: "Académie - Informatique"
  },
  {
    id: "imprimer-etiquettes",
    title: "Imprimer des étiquettes",
    description: "Guide pour imprimer des étiquettes",
    href: "/communaute/academie/procedures/informatique/imprimer-etiquettes",
    type: "tile",
    category: "Académie - Informatique"
  },
  {
    id: "ajouter-valeur-dictionnaire",
    title: "Ajouter une valeur dans le dictionnaire",
    description: "Procédure pour ajouter une valeur au dictionnaire",
    href: "/communaute/academie/procedures/informatique/ajouter-valeur-dictionnaire",
    type: "tile",
    category: "Académie - Informatique"
  },

  // Procédures Magasin
  {
    id: "reception-commandes",
    title: "Réception de commandes",
    description: "Guide pour la réception et le traitement des commandes",
    href: "/communaute/academie/procedures/magasin/reception-commandes",
    type: "tile",
    category: "Académie - Magasin"
  },

  // Procédures RH
  {
    id: "salaire",
    title: "Salaire",
    description: "Procédure pour la gestion des salaires",
    href: "/communaute/academie/procedures/rh/salaire",
    type: "tile",
    category: "Académie - RH"
  },

  // Compte utilisateur
  {
    id: "account",
    title: "Mon Compte",
    description: "Gérez vos informations personnelles et paramètres",
    href: "/account",
    type: "page",
    category: "Compte"
  },
  {
    id: "account-infos",
    title: "Informations personnelles",
    description: "Modifiez vos informations de profil",
    href: "/account/informations",
    type: "page",
    category: "Compte"
  },
  {
    id: "account-commandes",
    title: "Mes commandes",
    description: "Consultez l'historique de vos commandes",
    href: "/account/commandes",
    type: "page",
    category: "Compte"
  },
  {
    id: "account-factures",
    title: "Mes factures",
    description: "Téléchargez et consultez vos factures",
    href: "/account/factures",
    type: "page",
    category: "Compte"
  },
  {
    id: "account-adresses",
    title: "Mes adresses",
    description: "Gérez vos adresses de livraison et de facturation",
    href: "/account/adresses",
    type: "page",
    category: "Compte"
  },

  // Authentification
  {
    id: "signin",
    title: "Se connecter",
    description: "Connectez-vous à votre compte",
    href: "/signin",
    type: "page",
    category: "Authentification"
  },
  {
    id: "signup",
    title: "S'inscrire",
    description: "Créez un nouveau compte",
    href: "/signup",
    type: "page",
    category: "Authentification"
  }
];

export default function SearchBubble() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  // Fonction pour normaliser le texte (supprimer les accents)
  const normalizeText = (text: string) => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  // Recherche en temps réel
  useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      return;
    }

    const normalizedQuery = normalizeText(query);

    const filtered = SEARCH_INDEX.filter(item =>
      normalizeText(item.title).includes(normalizedQuery) ||
      normalizeText(item.description).includes(normalizedQuery) ||
      (item.category && normalizeText(item.category).includes(normalizedQuery))
    );

    setResults(filtered.slice(0, 8)); // Limite à 8 résultats
    setSelectedIndex(0);
  }, [query]);

  // Gestion des raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K pour ouvrir
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      
      // Échap pour fermer
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery("");
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus automatique sur l'input
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Navigation au clavier
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
        break;
    }
  };

  const handleSelect = (item: SearchItem) => {
    setIsOpen(false);
    setQuery("");
    router.push(item.href);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setQuery("");
    }
  };

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={handleToggle}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-out hover:scale-110 active:scale-95 flex items-center justify-center group"
        aria-label="Recherche globale"
      >
        <Search className="w-6 h-6 transition-transform group-hover:scale-110" />
      </button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-50 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Modal de recherche */}
      {isOpen && (
        <div 
          ref={bubbleRef}
          className="fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Rechercher une page ou fonctionnalité..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 outline-none text-gray-900 placeholder-gray-400"
              />
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Résultats */}
          <div className="max-h-80 overflow-y-auto">
            {query.trim().length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Tapez pour rechercher...</p>
                <p className="text-xs text-gray-400 mt-1">Utilisez Cmd+K pour ouvrir rapidement</p>
              </div>
            ) : results.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <p className="text-sm">Aucun résultat trouvé</p>
                <p className="text-xs text-gray-400 mt-1">Essayez avec d&apos;autres mots-clés</p>
              </div>
            ) : (
              <div className="py-2">
                {results.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                      index === selectedIndex ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {item.title}
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                          {item.type === 'page' ? 'Page' : 'Fonctionnalité'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {item.description}
                      </p>
                      {item.category && (
                        <p className="text-xs text-gray-400 mt-1">
                          {item.category}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>↑↓ Naviguer</span>
              <span>↵ Sélectionner</span>
              <span>Esc Fermer</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
